import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, LayoutGrid, ShieldCheck, Truck, Gauge, Star, Sparkles } from "lucide-react";
import { DEFAULT_WA_MESSAGE, fullUrl } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useSettings, useContact } from "../../hooks/useSettings";

const HERO_IMG = "/assets/shop/shop-2.jpg";

const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.14, delayChildren: 0.25 } },
};

const lineReveal = {
    hidden: { y: "115%" },
    show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const STAT_ICONS = [Star, Truck, Gauge, ShieldCheck];

export const HeroSection = () => {
    const ref = useRef(null);
    const { t, tr, lang, fs } = useLang();
    const settings = useSettings();
    const heroImg = settings.hero_image ? fullUrl(settings.hero_image) : HERO_IMG;
    const slogan = settings[lang === "ar" ? "slogan_ar" : "slogan_en"] || t("hero_slogan");
    const { waUrl } = useContact();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

    return (
        <section id="home" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16">
            <div className="absolute inset-0 bg-[radial-gradient(90%_70%_at_70%_10%,rgba(37,60,150,0.45)_0%,rgba(10,18,40,0)_60%)]" />
            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-red-600/10 blur-[140px]" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center w-full">
                <motion.div variants={container} initial="hidden" animate="show">
                    <motion.div variants={fadeUp} className="inline-flex items-center gap-2 border border-amber-400/30 bg-amber-400/5 rounded-full px-4 py-1.5 mb-7">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                        <span className={`text-xs font-semibold text-amber-300 ${fs("hero_badge")}`}>
                            {t("hero_badge")}
                        </span>
                    </motion.div>

                    <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.15] tracking-tight">
                        <span className="block overflow-hidden pb-1">
                            <motion.span variants={lineReveal} className={`block text-white ${fs("hero_t1")}`}>
                                {t("hero_t1")}
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span variants={lineReveal} className={`block text-transparent bg-clip-text bg-gradient-to-l from-amber-300 via-amber-400 to-yellow-600 ${fs("hero_t2")}`}>
                                {t("hero_t2")}
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-1">
                            <motion.span variants={lineReveal} className={`block text-2xl sm:text-3xl lg:text-4xl text-slate-400 font-bold mt-2 ${fs("hero_t3")}`}>
                                {t("hero_t3")}
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        variants={fadeUp}
                        data-testid="hero-slogan"
                        className={`mt-5 flex items-center gap-2.5 font-display font-extrabold text-xl sm:text-2xl text-transparent bg-clip-text bg-gradient-to-l from-amber-200 via-amber-400 to-yellow-500 ${fs("hero_slogan")}`}
                    >
                        <Sparkles className="w-5 h-5 text-amber-400 shrink-0" />
                        {slogan}
                    </motion.p>

                    <motion.p variants={fadeUp} className={`mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl ${fs("hero_sub")}`}>
                        {t("hero_sub")}
                    </motion.p>

                    <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-4">
                        <a
                            href="#inquiry"
                            data-testid="hero-cta-catalog-button"
                            className={`group flex items-center gap-2.5 bg-amber-400 text-[#0A1228] font-extrabold text-sm sm:text-base rounded-full px-7 py-3.5 hover:bg-amber-300 transition-colors duration-300 shadow-[0_0_35px_rgba(234,179,8,0.25)] ${fs("hero_cta_order")}`}
                        >
                            <LayoutGrid className="w-5 h-5 transition-transform duration-300 group-hover:-translate-y-0.5" />
                            {t("hero_cta_order")}
                        </a>
                        <a
                            href={waUrl(DEFAULT_WA_MESSAGE)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="hero-cta-whatsapp-button"
                            className={`flex items-center gap-2.5 border border-emerald-500/50 text-emerald-400 font-bold text-sm sm:text-base rounded-full px-7 py-3.5 hover:bg-emerald-500 hover:text-[#0A1228] transition-colors duration-300 ${fs("hero_cta_wa")}`}
                        >
                            <MessageCircle className="w-5 h-5" />
                            {t("hero_cta_wa")}
                        </a>
                    </motion.div>

                    <motion.div variants={fadeUp} className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {tr.hero_stats.map((label, i) => {
                            const Icon = STAT_ICONS[i];
                            return (
                                <div
                                    key={label}
                                    className="spotlight-card border border-white/10 rounded-2xl px-3 py-4 text-center hover:border-amber-400/40 transition-colors duration-300"
                                >
                                    <Icon className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                                    <p className={`text-xs font-bold text-slate-200 ${fs("hero_stats")}`}>{label}</p>
                                </div>
                            );
                        })}
                    </motion.div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="relative hidden lg:block"
                >
                    <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
                        <motion.img
                            src={heroImg}
                            alt="واجهة شركة إطار الثريا لخدمات السيارات ليلاً"
                            style={{ y: imgY, scale: imgScale }}
                            className="w-full h-[560px] object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1228] via-transparent to-transparent" />
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.1, duration: 0.7 }}
                        className="absolute -bottom-6 right-6 left-6 backdrop-blur-xl bg-[#101B3D]/85 border border-white/10 rounded-2xl p-4 flex items-center justify-between"
                        data-testid="hero-spec-hud"
                    >
                        <div>
                            <p className="text-[10px] font-mono-spec tracking-widest text-amber-400" dir="ltr">
                                AL THURAYA TIRE AUTO SERVICES CO
                            </p>
                            <p className={`font-display font-bold text-white text-sm ${fs("hero_hud_line")}`}>
                                {t("hero_hud_line")}
                            </p>
                        </div>
                        <p className={`font-display font-bold text-amber-300 text-sm ${fs("hero_hud_right")}`}>
                            {t("hero_hud_right")}
                        </p>
                    </motion.div>

                    <motion.div
                        animate={{ y: [0, -12, 0] }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute -top-5 -left-5 backdrop-blur-xl bg-[#101B3D]/90 border border-red-500/30 rounded-2xl px-4 py-3"
                    >
                        <p className="font-display font-bold text-xs text-red-400">{t("hero_float_t")}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{t("hero_float_s")}</p>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};
